<script setup>
import moment from 'moment'
import { getI18n } from '@/plugins/i18n'
import { menuRoutesEnum, getDocumentRouteByDocumentType } from '@/router'

import 'devextreme-vue/text-area'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { DxSplitter, DxItem } from 'devextreme-vue/splitter'
import { DxTabPanel } from 'devextreme-vue/tab-panel'
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'

import { useWebStreamWs } from '@/ws/web-stream.ws'

import * as dataGridColumn from '@/composables/data-grid-column'
import * as dataGrid from '@/composables/data-grid'
import { useDocumentIdentifiers } from '@/composables/document'
import { useColumns, useDialogTitle } from '@/composables/garage/worksheet-grid'
import { usePrint } from '@/composables/print'
import { useLookup } from '@/composables/data-grid'
import { usePartialSave } from '@/composables/partial-save'
import { useMultiSelectFilter, useMultiSelectEditorOptions } from '@/composables/data-grid'
import { useAddressRef, useLabelController } from '@/composables/data-grid-column'
import { useNotify } from '@/composables/notify'
import { clearRemovedRoundingRowFlag } from '@/composables/rounding-row'
import { useWebSocket } from '@/composables/web-socket'
import { useAttachment } from '@/composables/registry/service-attachment-grid'
import { useInfoBikeSearch } from '@/composables/registry/info-bike-search'
import { minutesToHHmm } from '@/lib/utils'

import { useGarageStore } from '@/stores/garage.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { usePortalStore } from '@/stores/portal.store'
import { useSaleStore } from '@/stores/sale.store'

import AttachmentIconList from '@/components/shared/AttachmentIconList.vue'
import CarServiceNumberBox from '@/components/shared/CarServiceNumberBox.vue'
import CityDropDownBox from '@/components/shared/CityDropDownBox.vue'
import CountryDropDownBox from '@/components/shared/CountryDropDownBox.vue'
import CustomerDropDownBox from '@/components/shared/CustomerDropDownBox.vue'
import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import CustomToolbarDeleteButtonTemplate from '@/components/shared/CustomToolbarDeleteButtonTemplate.vue'
import DepositDropDownBox from '@/components/shared/DepositDropDownBox.vue'
import DocumentGenerateDialog from '@/components/shared/DocumentGenerateDialog.vue'
import DocumentTagsTagBox from '@/components/shared/DocumentTagsTagBox.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'
import GridLayoutConfigTemplate from '@/components/shared/GridLayoutConfigTemplate.vue'
import InfoBikeSearchCaptionButton from '@/components/shared/InfoBikeSearchCaptionButton.vue'
import InfoBikePlateNumberEditor from '@/components/shared/InfoBikePlateNumberEditor.vue'
import InformationToolTip from '@/components/shared/InformationToolTip.vue'
import MechanicDropDownBox from '@/components/shared/MechanicDropDownBox.vue'
import MechanicsAvatarList from '@/components/shared/MechanicsAvatarList.vue'
import MultiSelectFilter from '@/components/shared/MultiSelectFilter.vue'
import ProgressStatusDropDownBox from '@/components/shared/ProgressStatusDropDownBox.vue'
import RecapPanel from '@/components/shared/RecapPanel.vue'
import ReferenceLink from '@/components/shared/ReferenceLink.vue'
import StateDropDownBox from '@/components/shared/StateDropDownBox.vue'
import VehicleDropDownBox from '@/components/shared/VehicleDropDownBox.vue'
import WorksheetRowGrid from '@/components/garage/WorksheetRowGrid.vue'
import WorksheetRowWorkTimeEntryGrid from '@/components/garage/WorksheetRowWorkTimeEntryGrid.vue'
import WorkTimeListPopup from '@/components/shared/WorkTimeListPopup.vue'
import InfoTooltipIcon from '@/components/shared/InfoTooltipIcon.vue'

import ChipStatus from '@/components/shared/ChipStatus.vue'
import PrintDialog from '@/components/shared/PrintDialog.vue'

import { useSubjectService } from '@/services/subject.service'
import { useEntityLock } from '@/services/entity-lock.service'
import { useDocumentService } from '@/services/document.service'

const props = defineProps({
  selectMode: {
    type: Boolean,
    default: false,
  }
})

const gridId = 'worksheet-grid'
const formInstance = ref(null)
const mainSplitterId = 'worksheet-grid-main-splitter'
const secondarySplitterId = 'worksheet-grid-secondary-splitter'

const isOwnerSaveDisabled = ref(true)

const print = usePrint()

const webStreamWs = useWebStreamWs()
const { createSocket } = useWebSocket()
const kanbanWebSocket = createSocket(webStreamWs.KANBAN_UPDATE_STREAM)

let generateDocumentToolbarButton = null
const showModal = ref(false)
const selectedRows = ref([])
const mechanics = ref([])
const worksheetId = ref(null)
const worksheetRowWorkTimeEntryGridRefreshKey = ref(0)
const refDocument = ref(null)
const generateDialogVisible = ref(false)
const generateDocumentType = ref(null)
const isPaid = ref(false)
const selectedInterventionTypeId = ref(null)
const includesVat = ref(false)

const { partialSave, isSaveDisabled, onRowValidating } = usePartialSave()
const { getAttachmentEntities } = useAttachment()
const { extractReferenceData } = useDocumentService()
const { addWarning: addDocumentWarning, displayWarnings: displayGenerateDocumentWarnings, checkPartialFields } = dataGrid.useGenerateDocumentWarning()
const { isVatCountryIta } = useSubjectService()
const {notifyError} = useNotify()
const { route, router } = dataGrid.useGridRouter()
const { repaintRow } = dataGrid.useUpdateMasterDetail()

const i18n = getI18n()
const registryStore = useRegistryStore()
const garageStore = useGarageStore()
const webSiteStore = useWebSiteStore()
const portalStore = usePortalStore()
const saleStore = useSaleStore()

let firstEditLoad = false

const {
  gridRef,
  refreshGridAsync,
  focusGrid,
  onFocusedRowChangedDebounced: onFocusedRowChanged,
} = dataGrid.useGridRef()

const { isVehicleSaveDisabled, onDocumentInitNewRowAsync, loadDocumentDetailsAsync, onDocumentEditorPreparing, onDocumentSaving, onDocumentRowUpdating, getDocumentTags } = useDocumentIdentifiers()
const { applyFilter, setMultiSelectFilterRef, clearMultiSelectFilter } = useMultiSelectFilter()
const { currentCountryId, currentVatCountryId: currentCustomerVatCountryId, currentStateId, currentZipCode, currentFilteredCitiesCount, setCellValueCity, setCellValueZipCode, onCountryChanged, onStateChanged } = useAddressRef({ onVatCountryChanged: () => updateFormLabels(editors, subjectLabelFields) })
const currentCustomerVatCountryCode = computed(() => {
  const country = webSiteStore.countries.find(x => x.id === currentCustomerVatCountryId.value)
  return country ? country.iso3_code : null
})

watch(() => webSiteStore.userDataFilter, () => gridRef.value.instance.refresh())

const entityLock = useEntityLock({
  baseLockUrl: garageStore.api.getAbsoluteUrl() + garageStore.api.worksheetsUrl,
  modelName: 'Worksheet',
  gridRef,
})

const allComputedTags = computed(() => {
  return getDocumentTags(isPaid, includesVat, selectedInterventionTypeId)
})

const rowButtons = entityLock.getEntityLockButtons({
  visible: {
    edit: (e) => !e.isReadonly && !(e.data?.has_rounding_expense_row || e.data?.has_rounding_row) && !entityLock.isLocked(false, e.entityId),
    delete: (e) => !e.isReadonly && !(e.data?.has_rounding_expense_row || e.data?.has_rounding_row) && !entityLock.isLocked(false, e.entityId),
    view: (e) => e.isReadonly || e.data?.has_rounding_expense_row || e.data?.has_rounding_row || entityLock.isLocked(false, e.entityId)
  }
})
rowButtons.push(
  {
    name: 'start',
    text: i18n.t('common.workTimeEntry', 2),
    icon: 'mdi-play',
    class: ['dx-link', 'dx-icon', 'mdi', 'mdi-play', 'dx-link-icon', 'bigger-font-icon'],
    onClick: async(e) => {
      showWorkTimeEntryPopupAsync({id: e.entityId})
    },
    visibleFn: (e) => {
      return !e.isReadonly && !e.data.has_active_mechanics && !(e.data?.has_rounding_expense_row || e.data?.has_rounding_row)
    }
  }
)
rowButtons.push(
  {
    name: 'start',
    text: i18n.t('common.workTimeEntry', 2),
    icon: 'mdi-timer',
    class: ['dx-link', 'dx-icon', 'mdi', 'mdi-timer', 'dx-link-icon', 'icon-orange'],
    onClick: async(e) => {
      showWorkTimeEntryPopupAsync({id: e.entityId})
    },
    visibleFn: (e) => {
      return !e.isReadonly && e.data.has_active_mechanics && !(e.data?.has_rounding_expense_row || e.data?.has_rounding_row)
    }
  }
)

const showWorkTimeEntryPopupAsync = (e) => {
  webSiteStore.api.getMechanicsActivitiesAsync({ worksheet: e.id })
    .then(response => {
      mechanics.value = response.data
      worksheetId.value = e.id
      showModal.value = true
    })
    .catch(error => {
      console.error('Error fetching mechanics activities:', error)
      notifyError(i18n.t('garage.fetchMechanicsActivitiesError'))
    })
}

defineExpose({ focusedRow: dataGrid.focusedRow })
const currentCustomerTypeId = ref(null)
const currentCustomerSubTypeId = ref(null)
const selectedDocument = ref(null)
const currentCarService = ref(null)
const currentVehicleFuelType = ref(null)
const vehicleSetValue = ref(null)
const editors = {}
const isReadOnly = ref(false)

const { customizeItem, updateFormLabels, registerLabelEditors } = useLabelController({ vehicleFuelType: currentVehicleFuelType, subjectType: currentCustomerTypeId, subjectSubTypeId: currentCustomerSubTypeId, vatCountryId: currentCustomerVatCountryId })
const subjectLabelFields = ['customer.name', 'customer.name_ext', 'customer.subject_sub_type_ref', 'customer.vat_number', 'customer.tax_code']
const {
  infoBikeSearchDisabled, showPulse, pulseCount,
  currentInfoBikeBrandCode, currentInfoBikeModelCode, extraData,
  disableInfoBikeSearch, onPlateBlur, infoBikeSearch, loadCreditsAsync,
  onInfoBikeEditorPreparing, infoBikeFormGroup
} = useInfoBikeSearch({
  getEditor: (field) => editors[field],
  getEditingData: () => gridRef.value?.instance?.getVisibleRows().find(row => row.isEditing)?.data,
  setVehicle: (vehicle, options) => vehicleSetValue.value?.(options?.preserveOwner ? { ...vehicle, preserveOwner: true, preservedCustomer: options.customer, preservedCustomerId: options.customerId } : vehicle),
  beginLoading: () => gridRef.value?.instance?.beginCustomLoading(),
  endLoading: () => gridRef.value?.instance?.endCustomLoading(),
  registryStore,
  garageStore,
  portalStore
})

const getFilterDataSource = (field) => {
  const buildFilterOptions = (items) => [{
    items: [...items]
  }]

  switch (field) {
    case 'document_progress_status':
      return buildFilterOptions(garageStore.getLocalizedDocumentProgressStatuses().filter(item => item.type !== portalStore.documentProgressStatusesEnum.PLANNED))
    case 'intervention_type_ref':
      return buildFilterOptions(garageStore.getLocalizedInterventionTypes())
    default:
      return []
  }
}

const onInitializedCustom = (e) => {
  disableInfoBikeSearch()
  if (isReadOnly.value && !dataGrid.isAdding.value) {
    const toolbarItems = e.component.option('toolbarItems') || []

    const updatedToolbarItems = toolbarItems.map((item, index) => {
      if (index === toolbarItems.length - 1) return item
      return {
        ...item,
        visible: false,
      }
    })

    e.component.option('toolbarItems', updatedToolbarItems)
  } else {
    entityLock.readOnlyToolbarItems(e)
  }
}

const currency = computed(() => {
  if (!selectedDocument.value) return null
  return webSiteStore.currencies.find(i => i.id === selectedDocument.value.currency_ref)
})

const columns = useColumns(
  editors,
  currentCountryId,
  currentStateId,
  currentCustomerTypeId,
  currentCustomerSubTypeId,
  currentCarService,
  currentVehicleFuelType,
  formInstance,
  includesVat,
  selectedInterventionTypeId,
  currentCustomerVatCountryId,
  currentCustomerVatCountryCode,
  currentInfoBikeBrandCode,
  currentInfoBikeModelCode,
  setCellValueZipCode,
  setCellValueCity
)

const editing = dataGrid.useDefaultEditing(useDialogTitle().dialogTitle, {
  colCount: 4,
  items: [
    {
      itemType: 'group',
      items: [
        {
          itemType: 'group',
          caption: i18n.t('common.worksheetData'),
          name: 'mdi-wrench',
          captionTemplate: 'generic-caption-template',
          colCount: 4,
          items: [
            { dataField: 'date', colSpan: 2 },
            { dataField: 'series', editorOptions: { readOnly: true } },
            { dataField: 'number_mnemonic', editorOptions: { readOnly: true } },
            { dataField: 'garage_reason_ref', colSpan: 3 },
            { dataField: 'car_service' },
            { dataField: 'deposit', colSpan: 2 },
            { dataField: 'garage', colSpan: 2 },
            { dataField: 'work_type', colSpan: 2 },
            { dataField: 'work_value', colSpan: 2 },
            { dataField: 'mechanics', colSpan: 2 },
            { dataField: 'expected_delivery_date', colSpan: 2 },
            { dataField: 'payment_type_ref', colSpan: 2 },
            { dataField: 'includes_vat', colSpan: 2 },
            { dataField: 'discount', editorOptions: { ...dataGrid.usePercentageOptions(), ...dataGrid.useMaxDiscountValueOptions() } },
          ]
        },
        {
          itemType: 'group',
          caption: i18n.t('common.documentStatus'),
          name: 'mdi-wrench',
          captionTemplate: 'generic-caption-template',
          colCount: 4,
          items: [
            { dataField: 'document_progress_status', colSpan: 2 },
            { itemType: 'empty', colSpan: 2 },
            {
              dataField: 'tags',
              colSpan: 4,
              tagTemplate: 'tagTemplate'
            }
          ]
        },
      ]
    },
    {
      itemType: 'tabbed',
      colSpan: 3,
      tabs: [
        {
          title: i18n.t('common.mainInfo'),
          items: [
            infoBikeFormGroup(i18n, garageStore),
            {
              itemType: 'group',
              caption: i18n.t('common.vehicleInfo'),
              name: 'mdi-motorbike',
              captionTemplate: 'vehicle-info-caption-template',
              validationGroup: 'vehicleGroup',
              colSpan: 3,
              colCount: 8,
              items: [
                { dataField: 'vehicle_id', colSpan: 2},
                { dataField: 'vehicle.supplier_brand_ref', label: { text: i18n.t('common.brandAlt') } },
                { dataField: 'vehicle.plate_number' },
                { dataField: 'vehicle.vin', editorOptions: { maxLength: 17 }, colSpan: 2 },
                { dataField: 'vehicle.infoBikeCodeFull', colSpan: 2 },
                { dataField: 'vehicle.name', colSpan: 2 },
                { dataField: 'vehicle.color', colSpan: 2 },
                { dataField: 'vehicle.km', editorOptions: { min: 0 } },
                { dataField: 'vehicle.engine_size', editorOptions: { min: 0 } },
                { dataField: 'vehicle.next_revision_due_date', colSpan: 2 },
              ]
            },
            {
              itemType: 'group',
              colCount: 3,
              colSpan: 3,
              cssClass: 'worksheet-owner-ctn',
              items: [
                {
                  itemType: 'group',
                  caption: i18n.t('common.ownerInfo'),
                  name: 'mdi-account-circle',
                  captionTemplate: 'owner-info-caption-template',
                  validationGroup: 'ownerGroup',
                  colSpan: 2,
                  colCount: 3,
                  items: [
                    { dataField: 'customer_id' },
                    { dataField: 'customer.subject_type_ref'},
                    { dataField: 'customer.subject_sub_type_ref'},
                    { dataField: 'customer.name', label: { text: i18n.t('common.nameAlt') } },
                    { dataField: 'customer.name_ext', label: { text: i18n.t('common.nameExtAlt') } },
                    { itemType: 'empty' },
                    ...dataGrid.useDefaultAddressEditing('customer.address', 1, null, {}, true),
                    { dataField: 'customer.email', label: { text: i18n.t('common.email') } },
                    { dataField: 'customer.mobile', label: { text: i18n.t('common.mobile') }, editorOptions: { onContentReady: (e) => { dataGrid.usePhoneMask(e.element, { definitions: dataGrid.useMask().mobileMaskRules }) } } },
                    { itemType: 'empty' },
                    { dataField: 'customer.vat_country_id', label: { text: i18n.t('common.vatCountry')} },
                    { dataField: 'customer.vat_number', label: { text: i18n.t('common.vatNumber')} },
                    { dataField: 'customer.tax_code', label: { text: i18n.t('common.taxCode') } },
                  ]
                },
                {
                  itemType: 'group',
                  caption: i18n.t('common.customerReport', 2),
                  name: 'mdi-notebook-edit',
                  captionTemplate: 'generic-caption-template',
                  items: [
                    {
                      dataField: 'notes',
                      editorType: 'dxTextArea',
                      editorOptions: { height: '20rem' }
                    },
                  ]
                },
              ]
            },
          ]
        },
        {
          title: i18n.t('common.billing'),
          items: [
            {
              itemType: 'group',
              caption: i18n.t('common.accounting'),
              colCount: 4,
              items: [
                { dataField: 'invoice_to_customer' },
                { dataField: 'single_invoice', label: { text: i18n.t('common.invoiceGrouping')} },
              ]
            },
            {
              itemType: 'group',
              caption: i18n.t('common.fiscalData'),
              name: 'mdi-receipt-text-outline',
              captionTemplate: 'generic-caption-template',
              colSpan: 3,
              colCount: 4,
              items: [
                { dataField: 'vat_type_ref' },
                { dataField: 'currency_ref' },
                { dataField: 'exchange_rate', editorOptions: { readOnly: true } },
                { dataField: 'exchange_rate_date', editorOptions: { readOnly: true } },
              ]
            }
          ]
        },
      ]
    }
  ],
  onInitialized: (e) => { formInstance.value = e.component },
  customizeItem: customizeItem,
}, {
  allowAdding: !props.selectMode,
  allowUpdating: (e) => !props.selectMode && !(e.data?.has_rounding_row || e.data?.has_rounding_expense_row),
  allowDeleting: (e) => !props.selectMode && !(e.data?.has_rounding_row || e.data?.has_rounding_expense_row),
  onInitializedCustom: (e) => onInitializedCustom(e),
  entityLock: entityLock
})

const readOnlyFields = [
  'vehicle.vin', 'vehicle.plate_number', 'vehicle.engine_code',
  'vehicle.color', 'vehicle.engine_size', 'vehicle.name',
  'vehicle.supplier_brand_ref'
]
const disabledOwnerFields = ['subject_type_ref', 'subject_sub_type_ref', 'name', 'mobile']
const addressRequiredFields = ['country', ['state', 'state_text'], 'zip_code', ['city', 'city_text'], 'street']
const excludedReadOnlyFields = ['expected_delivery_date', 'single_invoice', 'invoice_to_customer']

const makeFieldsReadOnly = (e, fields, readOnlyValue) => {
  if (e.parentType === 'dataRow' && fields.includes(e.dataField)) {
    e.editorOptions.disabled = !!readOnlyValue
  }
}

const onPopupCancel = (e) => {
  showModal.value = false
  worksheetId.value = null
}

const refreshSelectedWorksheet = async(targetWorksheetId) => {
  const updatedDocument = await repaintRow(gridRef, garageStore.api.getWorksheetByIdAsync, targetWorksheetId, targetWorksheetId)
  if (updatedDocument && selectedDocument.value?.id === targetWorksheetId) {
    selectedDocument.value = { ...selectedDocument.value, ...updatedDocument }
  }
  return updatedDocument
}

const onPopupSave = async(e) => {
  const updatedWorksheetId = worksheetId.value
  showModal.value = false
  worksheetId.value = null
  await refreshSelectedWorksheet(updatedWorksheetId)
  await refreshGridAsync()
  worksheetRowWorkTimeEntryGridRefreshKey.value += 1
}

const onInitNewRow = async(e) => {
  firstEditLoad = true
  currentCarService.value = null
  currentVehicleFuelType.value = null
  currentCustomerTypeId.value = null
  currentCustomerSubTypeId.value = null
  currentInfoBikeModelCode.value = null

  e.data.date = new Date()
  e.data.series = e.data.date.getFullYear().toString()
  e.data.status = 0
  isPaid.value = e.data?.sale_document?.paid

  if (registryStore.companyData.deposit) {
    e.data.deposit = registryStore.companyData.deposit
  }
  if (registryStore.companyData.garage) {
    e.data.garage = registryStore.companyData.garage
  }
  if (registryStore.companyData.default_currency_ref) {
    e.data.currency_ref = registryStore.companyData.default_currency_ref

    const currentCurrency = webSiteStore.currencies.find((currency) => currency.id === registryStore.companyData.default_currency_ref)
    if (currentCurrency) {
      e.data.exchange_rate = currentCurrency.exchange_rate
      e.data.exchange_rate_date = currentCurrency.exchange_rate_date
    }
  }

  e.data.work_type = garageStore.workTypesEnum.EXTERNAL
  e.data.work_value = garageStore.workValuesEnum.PRICE_LIST

  if (registryStore.companyData?.default_worksheet_garage_reason_ref) {
    const defaultGarageReasonRef = registryStore.companyData.default_worksheet_garage_reason_ref || registryStore.companyData.default_worksheet_portal_garage_reason_ref
    e.data.garage_reason_ref = defaultGarageReasonRef
    const defaultGarageReason = garageStore.garageReasons.find(i => i.id === defaultGarageReasonRef)
    e.data.intervention_type_ref = defaultGarageReason?.intervention_type_ref
    selectedInterventionTypeId.value = defaultGarageReason?.intervention_type_ref
    e.data.work_type = defaultGarageReason?.work_type
    e.data.work_value = defaultGarageReason?.work_value
  }

  e.data.document_progress_status = garageStore.documentProgressStatuses.filter(ps => ps.type === portalStore.documentProgressStatusesEnum.OPEN && ps.visible).sort((a, b) => a.order - b.order)[0]?.id

  e.promise = (async() => { await onDocumentInitNewRowAsync(e, garageStore.api.getWorksheetNextIdByDataAsync) })()
  e.data.includes_vat = includesVat.value = e.data.includes_vat ?? false

  await loadCreditsAsync()
}

const onDocumentGenerated = async() => {
  selectedDocument.value = await repaintRow(gridRef, garageStore.api.getWorksheetByIdAsync, selectedDocument.value?.id, selectedDocument.value?.id)
  gridRef.value?.instance?.clearSelection()
  await refreshGridAsync()
}

const isWorksheetCancelled = (worksheet) => {
  const progressStatus = garageStore.documentProgressStatuses.find(status => status.id === worksheet?.document_progress_status)
  return progressStatus?.type === portalStore.documentProgressStatusesEnum.CANCELLED
}

const onRowInserting = (e) => {
  if (e.data?.customer?.id) {
    delete e.data.customer.code
  }
}

const onRowRemoving = (e) => {
  selectedDocument.value = null
}

const onRowUpdating = (e) => {
  if (e.newData?.customer?.id) {
    delete e.newData.customer.code
  }
  if (e.newData?.customer && !e.newData.customer.id) {
    e.newData.customer.id = e.newData.customer_id = e.oldData.customer.id
  }
  if (e.newData?.vehicle && !e.newData.vehicle.id) {
    e.newData.vehicle.id = e.newData.vehicle_id = e.oldData.vehicle.id
  }
  onDocumentRowUpdating(e, selectedDocument)
}

const onEditCanceled = (e) => {
  isReadOnly.value = false
}

const onEditorPreparing = (e) => {
  if (firstEditLoad) {
    if (e.row?.data?.id) {
      firstEditLoad = false
      e.promise = loadDocumentDetailsAsync(garageStore.api.getWorksheetDetailsAsync, e.row, {
        currentVehicleFuelType,
        currentCustomerTypeId,
        currentCountryId,
        currentCustomerVatCountryId,
        currentStateId,
        includesVat,
        currentCarService,
        formInstance
      })
    } else {
      if (e.parentType === 'dataRow' && e.dataField === 'vehicle.vin') {
        firstEditLoad = false
        e.editorOptions.onInitialized = (e) => { setTimeout(() => { e.component.focus() }, 500) }
      }
    }
  }

  if (e.parentType === 'filterRow' && e.dataField === 'document_progress_status') {
    e.editorName = 'dxDropDownBox'
    e.editorOptions = useMultiSelectEditorOptions({
      dataSource: garageStore.getLocalizedDocumentProgressStatuses().filter(item => item.visible && item.type !== portalStore.documentProgressStatusesEnum.PLANNED),
      field: e.dataField,
    })
  }

  if (e.parentType === 'filterRow' && e.dataField === 'intervention_type_ref') {
    e.editorName = 'dxDropDownBox'
    e.editorOptions = useMultiSelectEditorOptions({
      dataSource: garageStore.getLocalizedInterventionTypes(),
      field: e.dataField,
    })
  }

  if (e.parentType === 'dataRow' && e.dataField === 'document_progress_status') {
    e.editorOptions.dataSource = garageStore.getLocalizedDocumentProgressStatuses().filter(item => item.visible && item.type !== portalStore.documentProgressStatusesEnum.PLANNED)
  }

  if (e.parentType === 'dataRow' && e.dataField === 'vat_type_ref') {
    e.editorOptions.readOnly = e.row.data?.rows_count > 0 || e.row.data?.customer?.vat_type_ref
  }
  if (e.parentType === 'dataRow' && (e.dataField === 'customer.name' || e.dataField === 'customer.name_ext' || e.dataField === 'customer.subject_type_ref' || e.dataField === 'customer.subject_sub_type_ref' ||
      e.dataField === 'customer.vat_country_id' || e.dataField === 'customer.vat_number' || e.dataField === 'customer.tax_code' || e.dataField === 'customer.address.zip_code' || e.dataField === 'customer.address.street' || e.dataField === 'customer.address.state'
  )) {
    e.editorOptions.disabled = !!e.row.data?.customer?.id || !!e.row.data?.customer_id
  }
  if (e.parentType === 'dataRow' && e.dataField === 'currency_ref') {
    e.editorOptions.onInitialized = (e) => { editors['currency_ref'] = e.component }
    e.editorOptions.readOnly = e.row.data?.rows_count > 0
  }
  if (e.parentType === 'dataRow' && e.dataField === 'paid_amount') {
    e.editorOptions.onInitialized = (e) => { editors['paid_amount'] = e.component }
    const currentCurrency = webSiteStore.currencies.find((currency) => currency.id === e.row.data?.currency_ref)
    if (currentCurrency) {
      e.editorOptions.format = dataGrid.useCurrencyOptions({ currency: currentCurrency }).format
    }
  }
  if (e.parentType === 'dataRow' && e.dataField === 'customer.vat_number') {
    e.editorOptions.onInitialized = (e) => { editors['customer.vat_number'] = e.component }
    e.editorOptions.disabled = !isVatCountryIta(currentCustomerVatCountryId.value) || e.row?.data?.id || e.row?.data?.customer?.id
  }
  if (e.parentType === 'dataRow' && e.dataField === 'customer.tax_code') {
    e.editorOptions.onInitialized = (e) => { editors['customer.tax_code'] = e.component }
  }
  registerLabelEditors(e, editors, ['customer.name', 'customer.name_ext', 'customer.subject_sub_type_ref'])
  if (e.parentType === 'dataRow' && e.dataField === 'customer.subject_type_ref') {
    const defaultValueChanged = e.editorOptions.onValueChanged
    e.editorOptions.onValueChanged = (args) => {
      currentCustomerTypeId.value = args.value
      currentCustomerSubTypeId.value = null
      defaultValueChanged(args)
      updateFormLabels(editors, subjectLabelFields)
    }
    e.editorOptions.onInitialized = (e) => { editors['customer.subject_type_ref'] = e.component }
  }
  if (e.parentType === 'dataRow' && (e.dataField === 'work_type' || e.dataField === 'work_value')) {
    e.editorOptions.readOnly = e.row.data?.id
  }
  if (e.parentType === 'dataRow' && e.dataField === 'car_service') {
    e.editorOptions.onInitialized = (e) => { editors['car_service'] = e.component }
    const interventionType = garageStore.getLocalizedInterventionTypes().find(i => i.id === e.row.data?.intervention_type_ref)
    e.editorOptions.disabled = !interventionType?.has_car_service
  }
  if (e.parentType === 'dataRow' && e.dataField === 'vehicle.vin') {
    const defaultValueChanged = e.editorOptions.onValueChanged

    e.editorOptions.onInitialized = (args) => {
      editors['vehicle.vin'] = args.component
    }

    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      const newValue = (args.value || '').toUpperCase()
      args.component.option('value', newValue)
    }
  }

  onInfoBikeEditorPreparing(e, editors)

  if (e.parentType === 'dataRow' && e.dataField === 'vehicle.supplier_brand_ref') {
    e.editorOptions.onInitialized = (e) => { editors['vehicle.supplier_brand_ref'] = e.component }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'vehicle.plate_number') {
    e.editorOptions.onInitialized = (e) => { editors['vehicle.plate_number'] = e.component }
    const defaultValueChanged = e.editorOptions.onValueChanged
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      let newValue = args.value || ''
      newValue = newValue.toUpperCase()
      if (!registryStore.companyData.vehicle_plate_allow_blank_spaces) {
        newValue = newValue.replace(/\s+/g, '')
      }
      args.component.option('value', newValue)
    }
  }
  if (e.parentType === 'dataRow' && e.dataField === 'vehicle.infoBikeCodeFull') {
    e.editorOptions.onInitialized = (e) => { editors['vehicle.infoBikeCodeFull'] = e.component }
  }
  if (e.parentType === 'dataRow' && e.dataField === 'vehicle.name') {
    e.editorOptions.onInitialized = (e) => { editors['vehicle.name'] = e.component }
  }
  if (e.parentType === 'dataRow' && e.dataField === 'vehicle.engine_size') {
    e.editorOptions.onInitialized = (e) => { editors['vehicle.engine_size'] = e.component }
  }
  if (e.parentType === 'dataRow' && e.dataField === 'vehicle.next_revision_due_date') {
    const defaultValueChanged = e.editorOptions.onValueChanged
    e.editorOptions.onValueChanged = (args) => {
      args.value = args.value && moment(args.value).format(dataGrid.useMask().dateSimpleFormat)
      defaultValueChanged(args)
    }
  }

  onDocumentEditorPreparing(e, editors, garageStore.api.getWorksheetNextIdByDataAsync, gridRef, excludedReadOnlyFields, portalStore.documentTypesEnum.GARAGE_WORKSHEET)
  if (e.parentType === 'dataRow' && (e.dataField === 'customer.subject_type_ref' || e.dataField === 'customer.subject_sub_type_ref' || e.dataField === 'customer.name' || e.dataField === 'customer.name_ext' ||
                                      e.dataField === 'customer.address.zip_code' || e.dataField === 'customer.address.street' || e.dataField === 'customer.address.state' ||
                                      e.dataField === 'customer.address.country' || e.dataField === 'customer.address.city' ||
                                      e.dataField === 'customer.mobile' || e.dataField === 'customer.tax_code')) {
    isOwnerSaveDisabled.value = isSaveDisabled('customer', disabledOwnerFields, gridRef, addressRequiredFields)
    const defaultValueChanged = e.editorOptions.onValueChanged
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      isOwnerSaveDisabled.value = isSaveDisabled('customer', disabledOwnerFields, gridRef, addressRequiredFields)
    }
  }
  if (e.parentType === 'dataRow') {
    makeFieldsReadOnly(e, readOnlyFields, e.row.data?.vehicle?.id)
  }

  if (isReadOnly.value && e.parentType === 'dataRow') {
    e.editorOptions.readOnly = true
  }
}

const onVehicleChanged = async(e, cellInfo) => {
  const customer = (await registryStore.getOrFetchCustomersAsync()).find(c => c.id === e.value?.customer_id)
  currentCountryId.value = customer?.address?.country || null
  currentCustomerVatCountryId.value = customer?.vat_country_id || null

  if (cellInfo?.data?.infoBike && e.value?.plate_number !== cellInfo?.data?.infoBike?.plate_number) {
    cellInfo.data.infoBike.plate_number = null
  }

  currentInfoBikeBrandCode.value = null
  currentInfoBikeModelCode.value = null
  editors['infoBike.brand']?.option('value', null)
  editors['infoBike.model']?.option('value', null)
  editors['infoBike.variant']?.option('value', null)

  formInstance.value?.repaint()
}

const onEditingStart = async(e) => {
  firstEditLoad = true
  includesVat.value = e.data.includes_vat
  isPaid.value = e.data?.sale_document?.paid

  if (e.data?.customer?.address?.country) {
    currentCountryId.value = e.data.customer.address.country
  }
  if (e.data?.customer?.vat_country_id) {
    currentCustomerVatCountryId.value = e.data.customer.vat_country_id
  }
  if (e.data?.customer?.address?.state) {
    currentStateId.value = e.data.customer.address.state
  }
  if (e.data?.customer?.subject_type_ref) {
    currentCustomerTypeId.value = e.data.customer.subject_type_ref
  }
  if (e.data?.customer?.subject_sub_type_ref) {
    currentCustomerSubTypeId.value = e.data.customer.subject_sub_type_ref
  }
  if (e.data?.garage_reason_ref) {
    const currentGarageReason = garageStore.garageReasons.find(i => i.id === e.data?.garage_reason_ref)
    e.data.intervention_type_ref = currentGarageReason?.intervention_type_ref
    selectedInterventionTypeId.value = currentGarageReason?.intervention_type_ref
  }

  if (e.data?.has_sale_document || e.data?.has_rounding_expense_row || e.data?.has_rounding_row) {
    isReadOnly.value = true
    return
  }

  await loadCreditsAsync()
}

const onToolbarPreparing = (e) => {
  dataGrid.useDefaultToolbar(e, {
    allowBatchDelete: true,
    addButtonOptions: { hint: `${i18n.t('common.add')} ${i18n.t('common.worksheet')}` }
  })

  e.toolbarOptions.items.unshift(
    print.printToolbarWidget({ onClick: async() => {
      try {
        const filename = selectedRows.value.length === 1 ? i18n.t('common.worksheet') + '.pdf' : i18n.t('common.worksheet', 2) + '.zip'
        await garageStore.api.printWorksheetsAsync(filename, {filename: filename, ids: selectedRows.value.map(i => i.id), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone}, { skipErrorMessage: (e) => (e.response?.data?.warnings) })
      } catch (err) {
        handleWarningMessages(err)
      }
    }}),
    print.printOptionsToolbarWidget()
  )

  e.toolbarOptions.items.unshift(
    {
      location: 'after',
      widget: 'dxDropDownButton',
      options: {
        icon: 'textdocument',
        text: `${i18n.t('common.generate')}...`,
        disabled: true,
        useSelectMode: false,
        displayExpr: 'text',
        dropDownOptions: { width: '20rem' },
        onItemClick: async(e) => {
          if (selectedRows.value && selectedRows.value[0]) {
            generateDocumentType.value = e.itemData.id
            const generatingItem = (await (garageStore.api.getWorksheetByIdAsync(selectedRows.value[0].id)))?.data
            if (!generatingItem) {
              notifyError(i18n.t('error.item_not_found'))
              return
            }
            if (isWorksheetCancelled(generatingItem)) {
              notifyError(i18n.t('error.validation.document_progress_status'))
              return
            }

            const docName = `${i18n.t('common.worksheet')} ${generatingItem.series} - ${generatingItem.number_mnemonic}`

            if (selectedRows.value.some(row => row.has_sale_document)) { addDocumentWarning(docName, i18n.t('error.validation.related_document_already_exists')) }
            if (selectedRows.value.some(row => row.rows_count === 0)) { addDocumentWarning(docName, i18n.t('error.validation.no_related_rows_found')) }
            if (generatingItem.has_active_mechanics) { addDocumentWarning(docName, i18n.t('error.validation.ongoing_work_time_entry')) }

            await checkPartialFields(generateDocumentType.value, docName, selectedRows.value, addDocumentWarning)

            if (!displayGenerateDocumentWarnings()) {
              refDocument.value = structuredClone(generatingItem)
              refDocument.value.generating_document_date = refDocument.value?.date
              refDocument.value.date = moment(new Date()).format('YYYY-MM-DD')
              refDocument.value.worksheet_number_mnemonic = ''
              refDocument.value.invoicing_customer = registryStore.customers.find(rc => rc.id === refDocument.value.invoice_to_customer_id) ?? null
              refDocument.value.sale_department = portalStore.saleDepartments.find(sd => sd.code === webSiteStore.saleDepartmentCodeEnum.WORKSHOP)?.id ?? null

              if (generateDocumentType.value === portalStore.documentGenerateEnum.IMMEDIATE_INVOICE) {
                refDocument.value.sale_document_type = portalStore.documentTypesEnum.SALE_IMMEDIATE_INVOICE
              }
              if (generateDocumentType.value === portalStore.documentGenerateEnum.DEFERRED_INVOICE) {
                refDocument.value.sale_document_type = portalStore.documentTypesEnum.SALE_INVOICE
              }
              if (generateDocumentType.value === portalStore.documentGenerateEnum.CORRESPONDENT) {
                refDocument.value.sale_document_type = portalStore.documentTypesEnum.SALE_CORRESPONDENT
              }
              generateDialogVisible.value = true
            }
          }
        },
        items: [
          { id: portalStore.documentGenerateEnum.CORRESPONDENT, text: i18n.t('common.documentType.sale_correspondent'), icon: 'mdi mdi-file-document-outline' },
          { id: portalStore.documentGenerateEnum.DEFERRED_INVOICE, text: i18n.t('common.documentType.sale_deferred_invoice'), icon: 'mdi mdi-receipt-text-outline' },
          { id: portalStore.documentGenerateEnum.IMMEDIATE_INVOICE, text: i18n.t('common.documentType.sale_immediate_invoice'), icon: 'mdi mdi-receipt-text-outline' },
        ],
        onInitialized: (e) => { generateDocumentToolbarButton = e.component }
      },
    }
  )

  e.toolbarOptions.items.unshift(dataGrid.gridLayoutConfigToolbarItem)
}

const batchDeleteAsync = async(selectedIds) => {
  await garageStore.api.batchDeleteWorksheetsAsync(selectedIds)
  selectedDocument.value = null
}

const onSaving = (e) => {
  e.promise = (async() => { await onDocumentSaving(e, selectedDocument, { isOwnerSaveDisabled: isOwnerSaveDisabled, isVehicleSaveDisabled: isVehicleSaveDisabled }) })()
}

const onSaved = async(e) => {
  await refreshGridAsync()
}

const onSelectionChanged = async(e) => {
  selectedRows.value = e.selectedRowsData
  generateDocumentToolbarButton.option(
    'disabled',
    e.selectedRowsData.length !== 1 || e.selectedRowsData.some(row => entityLock.isLocked(false, row.id) || isWorksheetCancelled(row))
  )
  print.printToolbarButton.value.option('disabled', e.selectedRowsData.length === 0)
  print.printOptionsToolbarButton.value.option('disabled', e.selectedRowsData.length === 0)
}

const onFocusedRowChangedDebounced = async(e) => {
  worksheetRowWorkTimeEntryGridRefreshKey.value += 1
  return onFocusedRowChanged(e, selectedDocument)
}

const isWorkTimeEntryReadOnly = (document) => !!document?.has_sale_document || !!document?.has_rounding_row || !!document?.has_rounding_expense_row

const onDetailRowSaved = async(parentId, removedRow) => {
  const wasWorkTimeEntryReadOnly = isWorkTimeEntryReadOnly(selectedDocument.value)
  clearRemovedRoundingRowFlag(selectedDocument.value, removedRow)
  const updatedDocument = await refreshSelectedWorksheet(parentId)
  isReadOnly.value = !!updatedDocument?.has_sale_document || !!updatedDocument?.has_rounding_row || !!updatedDocument?.has_rounding_expense_row
  if (wasWorkTimeEntryReadOnly !== isWorkTimeEntryReadOnly(updatedDocument)) { worksheetRowWorkTimeEntryGridRefreshKey.value += 1 }
}

const onDetailContentReady = (e) => gridRef.value.instance.updateDimensions()

const onClearFilter = () => { clearMultiSelectFilter() }

let gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: dataGrid.useODataSource(garageStore.api.getAbsoluteUrl() + garageStore.api.worksheetsUrl),
  columns: props.selectMode ? columns.filter(i => i.dataField !== 'quote.id') : columns,
  onClearFilter: onClearFilter,
  focusGrid: focusGrid,
  splittedGrid: true,
})

if (!props.selectMode) {
  gridOptions = {...gridOptions, ...{
    editing: editing,
    selection: dataGrid.useDefaultGridSelection(),
    onInitNewRow: onInitNewRow,
    onEditingStart: onEditingStart,
    onRowInserting: onRowInserting,
    onRowRemoving: onRowRemoving,
    onRowUpdating: onRowUpdating,
    onRowValidating: (e) => onRowValidating(e, gridRef, true, extraData),
    onEditorPreparing: onEditorPreparing,
    onSelectionChanged: onSelectionChanged,
    onFocusedRowChangedDebounced: onFocusedRowChangedDebounced,
    onSaving: onSaving,
    onSaved: onSaved,
    onToolbarPreparing: onToolbarPreparing,
    onEditCanceled: onEditCanceled,
    routerFilter: {
      fields: [{series: 'series'}, {number: 'number_mnemonic'}],
      getRoute: () => route,
      router: router
    }
  }}
}

const onSplitterResize = (e, splitterId) => {
  const totalSize = e.component.option('orientation') === 'vertical'
    ? e.component.element().offsetHeight
    : e.component.element().offsetWidth

  const sizes = e.component.option('items').map(item => {
    const px = parseFloat(item.size)
    const percentage = ((px / totalSize) * 100).toFixed(2) + '%'
    return { size: percentage }
  })
  webSiteStore.setSplitterState(splitterId, sizes)

  if (splitterId === mainSplitterId && selectedDocument.value && gridRef.value?.instance) {
    const gridInstance = gridRef.value.instance

    gridInstance.updateDimensions()
    gridInstance.repaint()

    const rowIndex = gridInstance.getRowIndexByKey(selectedDocument.value.id)
    if (rowIndex >= 0) {
      const rowHeight = gridInstance.option('rowHeight') || 37
      const scrollTop = rowIndex * rowHeight

      const scrollable = gridInstance.getScrollable()
      if (scrollable) {
        const currentScroll = scrollable.scrollOffset()
        scrollable.scrollTo({ x: currentScroll.left, y: scrollTop })
        gridInstance.repaint()
      }
    }
  }
}

const saveNewOwner = async() => {
  const grid = gridRef.value?.instance
  if (!grid) return

  const editingRow = grid.getVisibleRows().find(row => row.isEditing)
  if (!editingRow) return

  const rowIndex = editingRow.rowIndex
  const owner = editingRow.data?.customer

  if (!owner || owner.id || !owner.subject_sub_type_ref || !owner.subject_type_ref || !owner.name) {
    grid.getController('validating')?.validate(false)
    return
  }

  const nextIdResponse = await registryStore.api.getSubjectNextIdAsync()
  owner.code = nextIdResponse.data
  owner.type = registryStore.subjectEntityTypes.CUSTOMER
  owner.customer ??= {}
  owner.customer.discount_list_ref = registryStore.getLocalizedDiscountLists()[0]?.id
  delete owner.id
  delete owner.address

  const res = await partialSave(owner, registryStore.api.createSubjectAsync, { grid: grid })
  if (res?.customer) {
    const customer = (await registryStore.getOrFetchCustomersAsync()).find(c => c.id === res.customer.id)
    owner.id = customer.id
    grid.cellValue(rowIndex, 'customer_id', customer)
    formInstance.value?.repaint()
  }
}

const saveNewVehicle = async() => {
  const grid = gridRef.value?.instance
  if (!grid) return

  const editingRow = grid.getVisibleRows().find(row => row.isEditing)
  if (!editingRow) return

  const rowIndex = editingRow.rowIndex
  const vehicle = editingRow.data?.vehicle
  const customer = editingRow.data?.customer

  if (!vehicle || vehicle.id || !customer || !customer.id || (!vehicle.vin && !vehicle.plate_number) || !vehicle.name || !vehicle.supplier_brand_ref) {
    grid.getController('validating')?.validate(false)
    return
  }

  const nextIdResponse = await registryStore.api.getVehicleNextIdAsync()
  vehicle.id = nextIdResponse.data
  vehicle.status = registryStore.vehicleStatusTypesEnum.TRANSITED
  vehicle.customer_id = customer.id

  const res = await partialSave(vehicle, registryStore.api.createVehicleAsync, { grid: grid })
  if (res) {
    await registryStore.fetchVehiclesAsync(true)
    grid.cellValue(rowIndex, 'vehicle_id', res)
    formInstance.value?.repaint()
  }
}

const restoreSplitterSize = (e, splitterId) => {
  const saved = webSiteStore.splitterStates[splitterId]
  if (saved) {
    try {
      saved.forEach((item, index) => {
        e.component.option(`items[${index}].size`, item.size)
      })
    } catch (err) {
      console.warn(`Splitter "${splitterId}": data error.`)
    }
  }
}

const formItems = computed(() => {
  if (!selectedDocument.value || !currency.value) return []
  return [
    {
      dataField: 'intervention_type_ref',
      label: { text: i18n.t('common.interventionType') },
      editorType: 'dxSelectBox',
      editorOptions: {
        ...useLookup({ store: garageStore.getLocalizedInterventionTypes() }),
        placeholder: ''
      }
    },
    {
      dataField: 'status',
      label: { text: i18n.t('common.status') },
      editorType: 'dxSelectBox',
      editorOptions: {
        ...useLookup({ store: portalStore.getLocalizedDocumentStatuses() }),
        placeholder: ''
      }
    },
    {
      dataField: 'document_progress_status',
      label: { text: i18n.t('common.documentProgressStatus') },
      editorType: 'dxSelectBox',
      template: 'progressStatusTemplate',
      editorOptions: {
        ...useLookup({ store: garageStore.getLocalizedDocumentProgressStatuses().filter(item => item.type !== portalStore.documentProgressStatusesEnum.PLANNED) }),
        placeholder: ''
      }
    },
    {
      ...dataGridColumn.useTotalNoVatColumn({ currency: currency.value }),
      label: { text: dataGridColumn.useTotalNoVatColumn({ currency: currency.value }).caption },
      editorOptions: {
        format: { type: 'currency', currency: currency.value.code, precision: 2 }
      }
    },
    {
      ...dataGridColumn.useTotalVatValueColumn({ currency: currency.value }),
      label: { text: dataGridColumn.useTotalVatValueColumn({ currency: currency.value }).caption },
      editorOptions: {
        format: { type: 'currency', currency: currency.value.code, precision: 2 }
      }
    },
    {
      ...dataGridColumn.useTotalVatColumn({ currency: currency.value }),
      label: { text: dataGridColumn.useTotalVatColumn({ currency: currency.value }).caption },
      editorOptions: {
        format: { type: 'currency', currency: currency.value.code, precision: 2 }
      }
    }
  ]
})

const formRecapAdditionalSections = computed(() => {
  if (!selectedDocument.value || !currency.value) return []
  return [
    {
      title: i18n.t('common.workedTime'),
      sectionFormItems: [
        {
          dataField: 'total_work_time_minutes',
          label: { text: i18n.t('common.workedTime') },
          editorType: 'dxTextBox',
          editorOptions: {
            value: minutesToHHmm(selectedDocument.value?.total_work_time_minutes),
            readOnly: true
          }
        },
        {
          dataField: 'total_billable_time_minutes',
          label: { text: i18n.t('common.billableTime') },
          editorType: 'dxTextBox',
          editorOptions: {
            value: minutesToHHmm(selectedDocument.value?.total_billable_time_minutes),
            readOnly: true
          }
        }
      ]
    }
  ]
})

const updateWorksheetRowAsync = async(sender, response, message) => {
  const instance = gridRef.value?.instance
  if (!instance || !message.data?.worksheet) return
  const index = instance.getRowIndexByKey(message.data.worksheet.id)
  if (index < 0) return
  const row = instance.getVisibleRows()[index].data
  Object.assign(row, message.data.worksheet)
  instance.repaintRows([index])
}

onMounted(async() => await kanbanWebSocket.subscribe(updateWorksheetRowAsync))

onUnmounted(() => kanbanWebSocket.unsubscribe())

</script>
<template>
  <div>
    <print-dialog
      v-model:visible="print.printDialogVisible.value"
      :selected-rows="selectedRows"
      :entity="portalStore.printEntities.WORKSHEET"
      :print-function="garageStore.api.printWorksheetsAsync"
      :print-preview-function="(options) => garageStore.api.printPreviewWorksheetAsync(options)"
    />

    <document-generate-dialog
      v-if="generateDialogVisible"
      v-model:visible="generateDialogVisible"
      :generate-document-type="generateDocumentType"
      :generating-document-type="portalStore.documentGeneratingEnum.WORKSHEET"
      :generate-function="saleStore.generateSaleDocumentAsync"
      :next-id-function="(date, documentType) => saleStore.api.getSaleDocumentNextIdByDataAsync({ date, documentType })"
      :selected-item="refDocument"
      @created="onDocumentGenerated"
      @cancel="refQuote = null"
    />

    <dx-splitter
      orientation="vertical"
      height="90vh"
      @resize-end="e => onSplitterResize(e, mainSplitterId)"
      @item-collapsed="e => onSplitterResize(e, mainSplitterId)"
      @initialized="e => restoreSplitterSize(e, mainSplitterId)"
    >
      <dx-item
        min-size="21%"
        :collapsible="true"
        collapsed-size="21%"
      >
        <dx-data-grid
          ref="gridRef"
          v-bind="gridOptions"
          class="custom-padding-worksheet-grid"
        >
          <template #attachmentsCellTemplate="{ data: cellInfo }">
            <attachment-icon-list
              :data="cellInfo.data"
              document-name="common.worksheet"
              :has-attachments="cellInfo.data?.has_attachments"
              :document-field="'attachment_worksheet'"
              :form-data-source="{
                attachment_category: portalStore.attachmentCategoryEnum.GARAGE_DOCUMENT,
              }"
              :entities-id="getAttachmentEntities(cellInfo.data, ['worksheets', 'vehicle', 'subject'])"
              :refresh-grid-async="() => refreshGridAsync()"
            />
          </template>
          <template #gridLayoutConfigTemplate>
            <grid-layout-config-template
              :grid-ext-ref="gridRef"
              :grid-ext-id="gridId"
            />
          </template>
          <template #generic-caption-template="{ data }">
            <div class="dx-form-group-caption-wrapper">
              <q-icon
                :name="data.name"
                class="dx-form-group-caption-icon"
              />
              <span class="dx-form-group-caption-text">
                {{ data.caption }}
              </span>
            </div>
          </template>
          <template #vehicle-infoBike-search-caption-template="{ data }">
            <div
              class="dx-form-group-caption"
              style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
            >
              <div class="dx-form-group-caption-wrapper">
                <q-icon
                  :name="data.name"
                  class="dx-form-group-caption-icon"
                />
                <span class="dx-form-group-caption-text">
                  {{ i18n.t('common.infoBikeSearch', {
                    credits: garageStore.infoBikePlateSearchCredit?.credits ?? 0
                  }) }}
                </span>
              </div>
              <div>
                <info-bike-search-caption-button
                  :disabled="infoBikeSearchDisabled"
                  :show-pulse="showPulse"
                  :pulse-count="pulseCount"
                  :tooltip="i18n.t('common.infoBike')"
                  @search="infoBikeSearch"
                  @pulse-end="showPulse = false"
                />
              </div>
            </div>
          </template>
          <template #vehicle-info-caption-template="{ data }">
            <div
              class="dx-form-group-caption"
              style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
            >
              <div class="dx-form-group-caption-wrapper">
                <q-icon
                  :name="data.name"
                  class="dx-form-group-caption-icon"
                />
                <span class="dx-form-group-caption-text">
                  {{ data.caption }}
                </span>
              </div>
              <div>
                <q-btn
                  icon="mdi-content-save"
                  flat
                  size="xs"
                  round
                  :disable="isVehicleSaveDisabled"
                  @click="saveNewVehicle"
                >
                  <q-tooltip>
                    {{ i18n.t('common.fill_vehicle_fields_and_owner') }}
                  </q-tooltip>
                </q-btn>
              </div>
            </div>
          </template>
          <template #documentProgressStatus="{ data: cellInfo }">
            <progress-status-drop-down-box
              :data="cellInfo"
              :value="cellInfo?.data?.document_progress_status"
              :disabled="isReadOnly"
            />
          </template>

          <template #owner-info-caption-template="{ data }">
            <div
              class="dx-form-group-caption"
              style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
            >
              <div class="dx-form-group-caption-wrapper">
                <q-icon
                  :name="data.name"
                  class="dx-form-group-caption-icon"
                />
                <span class="dx-form-group-caption-text">
                  {{ data.caption }}
                </span>
              </div>
              <div>
                <q-btn
                  icon="mdi-content-save"
                  flat
                  size="xs"
                  round
                  :disable="isOwnerSaveDisabled"
                  @click="saveNewOwner"
                />
              </div>
            </div>
          </template>
          <template #quoteRef="{ data: cellInfo }">
            <reference-link
              v-if="!!cellInfo.data.quote?.id"
              :data="{
                series: cellInfo.data.quote?.series,
                number: cellInfo.data.quote?.number_mnemonic
              }"
              :text-fields="['prefix', 'series', 'number']"
              :query-fields="['series', 'number']"
              :target="menuRoutesEnum.QUOTES"
              :fetch-document-relations="() => garageStore.api.getWorksheetDocumentRelationTreeAsync(cellInfo.data.id)"
            >
              <information-tool-tip
                :data="extractReferenceData({ ...cellInfo.data?.quote, type: portalStore.documentTypesEnum.GARAGE_QUOTE })"
              />
            </reference-link>
          </template>
          <template #saleDocumentRef="{ data: cellInfo }">
            <reference-link
              v-if="!!cellInfo.data.sale_document?.id"
              :data="{
                series: cellInfo.data.sale_document?.series,
                number: cellInfo.data.sale_document?.number_mnemonic
              }"
              :text-fields="['prefix', 'series', 'number']"
              :query-fields="['series', 'number']"
              :target="getDocumentRouteByDocumentType(cellInfo.data.sale_document.type)"
              :fetch-document-relations="() => garageStore.api.getWorksheetDocumentRelationTreeAsync(cellInfo.data.id)"
            >
              <information-tool-tip
                :data="extractReferenceData(cellInfo.data?.sale_document)"
              />
            </reference-link>
          </template>
          <template #fieldInfoTooltipTemplate>
            <info-tooltip-icon
              :visible="!gridRef?.instance.getVisibleRows().find(row => row.isEditing)?.data?.vehicle?.plate_number && !gridRef?.instance.getVisibleRows().find(row => row.isEditing)?.data?.vehicle?.vin"
              :message="i18n.t('common.plateVinHelpInfo')"
            />
          </template>
          <template #serviceHistoryRef="{ data: cellInfo }">
            <reference-link
              v-if="!!cellInfo.data?.vehicle?.vin"
              :data="{
                prefix: cellInfo.data?.vehicle?.vin,
                vin: cellInfo.data?.vehicle?.vin,
              }"
              :text-fields="['prefix']"
              :query-fields="['vin']"
              :target="menuRoutesEnum.SERVICE_HISTORY"
            >
              <template #fieldInfoTooltipTemplate>
                <info-tooltip-icon
                  :visible="!cellInfo.data?.vehicle?.plate_number && !cellInfo.data?.vehicle?.vin"
                  :message="i18n.t('common.plateVinHelpInfo')"
                />
              </template>
            </reference-link>
          </template>
          <template #customerEditor="{ data: cellInfo }">
            <customer-drop-down-box
              show-clear-button
              :data="cellInfo"
              :disabled="isReadOnly || (cellInfo.data?.rows_count > 0 && cellInfo.column?.dataField !== 'invoice_to_customer')"
            />
          </template>
          <template #infoBikePlateNumberEditor="{ data }">
            <info-bike-plate-number-editor
              :cell-info="data"
              :readonly="isReadOnly || data.data?.rows_count > 0 || !!data.data?.vehicle?.id"
              @update="v => data.setValue(v)"
              @blur="() => onPlateBlur()"
            />
          </template>
          <template #vehicleEditor="{ data: cellInfo }">
            <vehicle-drop-down-box
              show-clear-button
              :data="cellInfo"
              :disabled="isReadOnly || cellInfo.data?.rows_count > 0"
              @vue:mounted="vehicleSetValue = cellInfo.setValue"
              @value-changed="onVehicleChanged($event, cellInfo)"
            />
          </template>
          <template #countryEditor="{ data: cellInfo }">
            <country-drop-down-box
              :data="cellInfo"
              :is-portal="['customer.vat_country_id'].includes(cellInfo.column?.dataField)"
              :disabled="!!cellInfo.data.customer?.id || isReadOnly"
              @value-changed="onCountryChanged($event, cellInfo.column?.dataField)"
            />
          </template>
          <template #vatCountryEditor="{ data: cellInfo }">
            <country-drop-down-box
              :data="cellInfo"
              :is-portal="['customer.vat_country_id'].includes(cellInfo.column?.dataField)"
              :disabled="!!cellInfo.data.customer?.id || isReadOnly"
              @value-changed="onCountryChanged($event, cellInfo.column?.dataField, type='vat_country')"
            />
          </template>
          <template #stateEditor="{ data: cellInfo }">
            <state-drop-down-box
              :data="cellInfo"
              :value="cellInfo.data.customer?.address?.state"
              :value-text="cellInfo.data.customer?.address?.state_text"
              :country="currentCountryId"
              :disabled="!!cellInfo.data.customer?.id || isReadOnly"
              @value-changed="onStateChanged($event)"
            />
          </template>
          <template #cityEditor="{ data: cellInfo }">
            <city-drop-down-box
              :data="cellInfo"
              :value="cellInfo.data.customer?.address?.city"
              :value-text="cellInfo.data.customer?.address?.city_text"
              :country="currentCountryId"
              :state="currentStateId"
              :zip-code="currentZipCode"
              :filtered-cities-count="currentFilteredCitiesCount"
              :disabled="!!cellInfo.data.customer?.id || isReadOnly"
            />
          </template>
          <template #depositEditor="{ data: cellInfo }">
            <deposit-drop-down-box
              :data="cellInfo"
              :disabled="isReadOnly || cellInfo.data?.rows_count > 0"
            />
          </template>
          <template #mechanicsEditor="{ data }">
            <mechanic-drop-down-box
              :data="data"
              :disabled="isReadOnly"
            />
          </template>
          <template #mechanicsAvatarList="{ data }">
            <mechanics-avatar-list :ids="data.value" />
          </template>
          <template #documentProgressStatusCellTemplate="{ data: templateOptions }">
            <chip-status :data="garageStore.getLocalizedDocumentProgressStatuses().find(x => x.id === templateOptions.data.document_progress_status)" />
          </template>
          <template #multiSelectFilterRef="{ data }">
            <multi-select-filter
              :ref="el => setMultiSelectFilterRef(data.component.option('field'), el)"
              :data-source="getFilterDataSource(data.component.option('field'))"
              :field="data.component.option('field')"
              :grid-ref="gridRef"
              :apply-filter="applyFilter"
              v-bind="{ data }"
            />
          </template>
          <!-- <template #tagTemplate="{ data }">
            <q-chip
              size="sm"
              class="q-ma-none"
              text-color="white"
              style="margin: 0"
              :label="data.name"
              :style="{ backgroundColor: data.color }"
            />
          </template>
          <template #tagsEditor>
            <dx-tag-box
              :items="allComputedTags"
              :value="allComputedTags.map(x => x.id)"
              :apply-value-mode="'useButtons'"
              :multiline="true"
              value-expr="id"
              display-expr="name"
              :disabled="true"
            >
              <template #tagTemplate="{data: cellInfo}">
                <ChipStatus :data="allComputedTags.find(x => x.id === cellInfo?.id)" />
              </template>
            </dx-tag-box>
          </template> -->
          <template #tagsEditor>
            <document-tags-tag-box
              :tags="allComputedTags"
              :value="allComputedTags.map(x => x.id)"
              disabled
            />
          </template>
          <template
            #editButtonsTemplate="{ data: cellInfo }"
          >
            <edit-lock-buttons-template
              :entity-name="'Worksheet'"
              :cell-info="cellInfo"
              :grid="gridRef"
              :entity-lock="entityLock"
              :is-locked-available="true"
              :buttons="rowButtons"
            />
          </template>
          <template #customToolbarAddButtonTemplate>
            <custom-toolbar-add-button-template
              :grid="gridRef"
              :entity-lock="entityLock"
            />
          </template>
          <template #customToolbarDeleteButtonTemplate>
            <custom-toolbar-delete-button-template
              :selected-rows-id="selectedRows?.map(i => i.id) || []"
              :grid="gridRef"
              :entity-lock="entityLock"
              :on-delete="batchDeleteAsync"
              :disabled="selectedRows?.some(i => i.has_sale_document || i.has_rounding_expense_row || i.has_rounding_row) || selectedRows.length === 0"
            />
          </template>
          <template #carServiceNumberEditor="{ data: cellInfo }">
            <car-service-number-box
              :data="cellInfo"
              :value="cellInfo.value"
              :disabled="isReadOnly ||cellInfo.data?.rows_count > 0"
              :garage-reason-ref="cellInfo.data.garage_reason_ref"
              :current-car-service="currentCarService"
            />
          </template>
        </dx-data-grid>
      </dx-item>
      <dx-item
        min-size="21%"
        :collapsible="true"
        collapsed-size="21%"
      >
        <dx-splitter
          orientation="horizontal"
          @resize-end="e => onSplitterResize(e, secondarySplitterId)"
          @item-collapsed="e => onSplitterResize(e, secondarySplitterId)"
          @initialized="e => restoreSplitterSize(e, secondarySplitterId)"
        >
          <dx-item
            :collapsible="true"
            min-size="50%"
          >
            <div style="height: 100%; width: 100%;">
              <dx-tab-panel
                :height="'100%'"
                :defer-rendering="false"
              >
                <dx-item :title="$t('common.row', 2)">
                  <div style="height: 100%;">
                    <worksheet-row-grid
                      v-if="selectedDocument"
                      :data="selectedDocument"
                      :entity-lock="entityLock"
                      :read-only="selectedDocument.has_sale_document || (entityLock.isLocked(false, selectedDocument.id) && !entityLock.isLocked(true, selectedDocument.id)) || selectedDocument.has_rounding_row"
                      class="custom-padding-worksheet-row-grid"
                      @row-saved="onDetailRowSaved"
                      @content-ready="onDetailContentReady"
                    />
                    <div
                      v-else
                      style="min-height: 1px;"
                    />
                  </div>
                </dx-item>
                <dx-item :title="$t('common.workTimeEntry', 2)">
                  <div style="height: 100%;">
                    <worksheet-row-work-time-entry-grid
                      v-if="selectedDocument"
                      :key="worksheetRowWorkTimeEntryGridRefreshKey"
                      :data="selectedDocument"
                      :entity-lock="entityLock"
                      :read-only="selectedDocument.has_sale_document || (entityLock.isLocked(false, selectedDocument.id) && !entityLock.isLocked(true, selectedDocument.id)) || selectedDocument.has_rounding_row || selectedDocument.has_rounding_expense_row"
                      class="custom-padding-worksheet-row-grid"
                      @row-saved="onDetailRowSaved"
                      @content-ready="onDetailContentReady"
                    />
                    <div
                      v-else
                      style="min-height: 1px;"
                    />
                  </div>
                </dx-item>
              </dx-tab-panel>
            </div>
          </dx-item>
          <dx-item
            :collapsible="true"
            size="15%"
          >
            <div style="height: 100%; width: 100%;">
              <recap-panel
                v-if="selectedDocument"
                ref="recapPanelRef"
                class="custom-padding-recap-panel"
                :main-title="i18n.t('common.documentTotals')"
                :form-data="selectedDocument"
                :form-items="formItems"
                :additional-sections="formRecapAdditionalSections"
                :time-key="`${selectedDocument.total_work_time_minutes}-${selectedDocument.total_billable_time_minutes}`"
              />
              <div
                v-else
                style="min-height: 1px;"
              />
            </div>
          </dx-item>
        </dx-splitter>
      </dx-item>
    </dx-splitter>
    <work-time-list-popup
      v-if="showModal"
      v-model:visible="showModal"
      :options="{ worksheetId: worksheetId, mechanics: mechanics, title: $t('common.workTimeEntry', 2), width: '31rem' }"
      @cancel="onPopupCancel"
      @save="onPopupSave"
    />
  </div>
</template>
<style scoped lang="scss">
.custom-padding-recap-panel {
  padding-top: 2rem;
  padding-left: 1.5rem;
}
.custom-padding-worksheet-grid {
  padding-bottom: 1.5rem;
}
.custom-padding-worksheet-row-grid {
  padding-right: 1.5rem;
}

.dx-form-group-caption-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dx-form-group-caption-wrapper .dx-form-group-caption-icon {
  font-size: 1.2rem;
  line-height: 1;
}

.dx-form-group-caption-wrapper .dx-form-group-caption-text {
  line-height: 1.2;
}

:deep(.icon-orange) {
  color: #ff8902 !important;
}

:deep(.bigger-font-icon) {
  font-size: 20px !important;
  margin-left: 2px !important;
}

</style>
